import { useState, useEffect, useCallback } from "react";
import { fetchRezervasyonlar, fetchMusteriler, fetchKaynaklar, type Rezervasyon, type Musteri, type Kaynak } from "@/services/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const RENKLER = ["#5a7a4a", "#4a8a8a", "#8a7a4a", "#6a8a6a", "#7a6a8a"];

export default function ReportsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rezervasyonlar, setRezervasyonlar] = useState<Rezervasyon[]>([]);
  const [musteriler, setMusteriler] = useState<Musteri[]>([]);
  const [kaynaklar, setKaynaklar] = useState<Kaynak[]>([]);
  const [aktifSekme, setAktifSekme] = useState("gelir");

  const loadData = useCallback(async () => {
    try { setLoading(true); setError(null);
      const [r, m, k] = await Promise.all([fetchRezervasyonlar(), fetchMusteriler(), fetchKaynaklar()]);
      setRezervasyonlar(r); setMusteriler(m); setKaynaklar(k);
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Hata"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const toplamGelir = rezervasyonlar.filter(r => !r.silindi).reduce((s, r) => s + Number(r.toplam_tutar || 0), 0);
  const toplamRez = rezervasyonlar.filter(r => !r.silindi).length;
  const ortalamaGece = toplamRez > 0 ? Math.round(rezervasyonlar.reduce((s, r) => s + r.gece_sayisi, 0) / toplamRez) : 0;

  const aylar = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  const yil = new Date().getFullYear();
  const aylikGelir = aylar.map((ay, i) => {
    const gelir = rezervasyonlar.filter(r => { const d = new Date(r.giris_tarihi); return d.getFullYear() === yil && d.getMonth() === i && !r.silindi; }).reduce((s, r) => s + Number(r.toplam_tutar || 0), 0);
    return { ay, gelir };
  });

  const kaynakData = kaynaklar.map(k => {
    const count = musteriler.filter(m => m.kaynak_id === k.id).length;
    return { name: k.ad, value: count };
  }).filter(x => x.value > 0).slice(0, 5);

  const odaTipiData = (() => {
    const map: Record<string, number> = {};
    rezervasyonlar.filter(r => !r.silindi).forEach(r => { const t = r.oda_tipi || "Diğer"; map[t] = (map[t] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value }));
  })();

  if (loading) return <div className="flex items-center justify-center h-64"><div className="flex flex-col items-center gap-3"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div><p className="text-sm text-foreground-500">Yükleniyor...</p></div></div>;
  if (error) return <div className="flex items-center justify-center h-64"><div className="flex flex-col items-center gap-4"><p className="text-sm text-foreground-600">{error}</p><button onClick={loadData} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap">Tekrar Dene</button></div></div>;

  return (
    <div className="space-y-5">
      <div><h1 className="text-xl font-semibold text-foreground-900 font-heading">Raporlar</h1><p className="text-sm text-foreground-500 mt-0.5">Otel performansı ve detaylı analiz</p></div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Toplam Gelir" value={`₺${toplamGelir.toLocaleString("tr-TR")}`} color="text-primary-700" />
        <SummaryCard label="Rezervasyon" value={toplamRez} />
        <SummaryCard label="Ort. Geceleme" value={`${ortalamaGece} gece`} color="text-accent-600" />
        <SummaryCard label="Aktif Kaynak" value={kaynaklar.length} color="text-secondary-700" />
      </div>

      <div className="flex gap-1 bg-background-100 p-1 rounded-lg w-fit flex-wrap">
        {[{ key: "gelir", label: "Gelir" }, { key: "kaynak", label: "Kaynak Analizi" }].map(s => (<button key={s.key} onClick={() => setAktifSekme(s.key)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${aktifSekme === s.key ? "bg-background-50 text-foreground-900" : "text-foreground-600 hover:text-foreground-800"}`}>{s.label}</button>))}
      </div>

      {aktifSekme === "gelir" && (
        <div className="space-y-5">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-foreground-800 mb-4 font-heading">Aylık Gelir (₺)</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={aylikGelir} barSize={28}><CartesianGrid strokeDasharray="3 3" stroke="#e8e4de" /><XAxis dataKey="ay" tick={{ fontSize: 11, fill: "#7a7060" }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 11, fill: "#7a7060" }} axisLine={false} tickLine={false} tickFormatter={v => `₺${(Number(v) / 1000).toFixed(0)}K`} /><Tooltip formatter={(v: number) => [`₺${v.toLocaleString("tr-TR")}`, "Gelir"]} /><Bar dataKey="gelir" fill="oklch(0.58 0.13 136)" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
          {odaTipiData.length > 0 && (
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4 font-heading">Oda Tipi Tercihi</h3>
              <div className="flex items-center gap-6"><PieChart width={140} height={140}><Pie data={odaTipiData} cx={65} cy={65} innerRadius={40} outerRadius={65} dataKey="value" stroke="none">{odaTipiData.map((_, i) => <Cell key={i} fill={RENKLER[i % RENKLER.length]} />)}</Pie></PieChart><div className="space-y-2">{odaTipiData.map((d, i) => (<div key={d.name} className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm shrink-0" style={{ background: RENKLER[i] }}></div><p className="text-xs text-foreground-700">{d.name}: {d.value}</p></div>))}</div></div>
            </div>
          )}
        </div>
      )}

      {aktifSekme === "kaynak" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-foreground-800 mb-4 font-heading">Kaynağa Göre Müşteri</h3>
            <div className="flex items-center gap-6"><PieChart width={160} height={160}><Pie data={kaynakData} cx={75} cy={75} innerRadius={45} outerRadius={75} dataKey="value" stroke="none">{kaynakData.map((_, i) => <Cell key={i} fill={RENKLER[i % RENKLER.length]} />)}</Pie></PieChart><div className="space-y-2">{kaynakData.map((d, i) => (<div key={d.name} className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm shrink-0" style={{ background: RENKLER[i] }}></div><p className="text-xs text-foreground-700">{d.name}: {d.value}</p></div>))}</div></div>
          </div>
          <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-foreground-800 mb-4 font-heading">Dönüşüm Raporu</h3>
            <div className="space-y-4">
              {[{ l: "Toplam Müşteri", v: musteriler.length }, { l: "Rezervasyon Yapan", v: rezervasyonlar.filter(r => !r.silindi).length }, { l: "Genel Dönüşüm", v: musteriler.length > 0 ? `%${Math.round((rezervasyonlar.filter(r => !r.silindi).length / musteriler.length) * 100)}` : "%0" }, { l: "Kapora Bekleyen", v: rezervasyonlar.filter(r => r.durum === "kapora_bekleniyor").length }].map(x => (<div key={x.l} className="flex items-center justify-between"><span className="text-sm text-foreground-700">{x.l}</span><span className="text-sm font-semibold text-foreground-900">{x.v}</span></div>))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color = "text-foreground-900" }: { label: string; value: string | number; color?: string }) {
  return <div className="bg-background-50 border border-background-200/70 rounded-lg p-4"><p className="text-xs text-foreground-500 mb-1">{label}</p><p className={`text-xl font-semibold font-heading ${color}`}>{value}</p></div>;
}